import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import { logoutUser } from './authService';
import { ConditionalOptionValues, InternalSignatureDocumentSignerRole } from '@dill/dill-shared';

// Internal signature documents are deliberately NOT wired into a slice/reducer (mirrors the `shareApplication`
// thunk in creditApplicationsService.ts) — they're read/written from one place at a time
// (CreditApplicationDetails' Internal Signature Documents card + its Sign/Send modals), which just keeps the
// fetched InternalSignatureDocument[] in local component state and re-fetches after a mutating call.

// One entry per fillable field being submitted at sign time — `fieldValue` is only present for a
// CONDITIONAL_SELECTED_FIELD (its sub-field answers; `value` there is just the selected option).
type SignedFieldValue = { id: string; value: any; fieldValue?: ConditionalOptionValues[] };

export const getInternalSignatureDocuments = createAsyncThunk(
  'internalSignatureDocument/getInternalSignatureDocuments',
  async (
    { supplierId, creditApplicationId }: { supplierId: string; creditApplicationId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalSignatureDocuments/getInternalSignatureDocuments/${creditApplicationId}`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error?.message);
    }
  }
);

export const sendInternalSignatureDocumentsForSignature = createAsyncThunk(
  'internalSignatureDocument/sendInternalSignatureDocumentsForSignature',
  async (
    { supplierId, creditApplicationId }: { supplierId: string; creditApplicationId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalSignatureDocuments/sendInternalSignatureDocumentsForSignature`,
        { creditApplicationId },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error?.message);
    }
  }
);

// Per-row "Send Reminder" trigger (Internal Signature Documents card) — only re-emails the candidates for one
// specific `role`, unlike sendInternalSignatureDocumentsForSignature above which resends every pending signer.
export const sendInternalSignatureDocumentReminder = createAsyncThunk(
  'internalSignatureDocument/sendInternalSignatureDocumentReminder',
  async (
    {
      supplierId,
      internalSignatureDocumentId,
      role,
    }: {
      supplierId: string;
      internalSignatureDocumentId: string;
      role: InternalSignatureDocumentSignerRole;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalSignatureDocuments/sendInternalSignatureDocumentReminder`,
        { internalSignatureDocumentId, role },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error?.message);
    }
  }
);

// Unauthenticated — powers the standalone SignInternalSignatureDocument page reached via an emailed link
// (the signer may have no app login at all, or only CREDIT_APP_SALES). Mirrors getCreditApplication
// in creditApplicationsService.ts (`headers: {}`, no Firebase token).
export const getInternalSignatureDocumentForSigning = createAsyncThunk(
  'internalSignatureDocument/getInternalSignatureDocumentForSigning',
  async (
    {
      internalSignatureDocumentId,
      email,
      role,
    }: { internalSignatureDocumentId: string; email: string; role: string },
    thunkApi
  ) => {
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/internalSignatureDocumentsPublic/getInternalSignatureDocumentForSigning/${internalSignatureDocumentId}`,
        { headers: {}, params: { email, role } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error?.message);
    }
  }
);

// Unauthenticated — powers the "Marketing Segment" (P21 Class 1) dropdown on the standalone
// SignInternalSignatureDocument page with P21's OWN live code list instead of whatever's typed
// into the field's optionList on the template, since that can go stale (see
// getClass1CodesP21Service on the backend). Same trust model as getInternalSignatureDocumentForSigning
// above — read-only, non-sensitive reference data, so no email/role eligibility check is needed.
export const getP21Class1CodesForSigning = createAsyncThunk(
  'internalSignatureDocument/getP21Class1CodesForSigning',
  async ({ internalSignatureDocumentId }: { internalSignatureDocumentId: string }, thunkApi) => {
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/internalSignatureDocumentsPublic/getP21Class1CodesForSigning/${internalSignatureDocumentId}`,
        { headers: {} }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error?.message);
    }
  }
);

// Unauthenticated counterpart to signInternalSignatureDocument below — used by the standalone SignInternalSignatureDocument
// page instead of the in-app modal. `ipAddress` isn't accepted here — the backend always takes it
// server-side from the request (see signInternalSignatureDocumentForSigningController), same as the in-app path.
export const signInternalSignatureDocumentForSigning = createAsyncThunk(
  'internalSignatureDocument/signInternalSignatureDocumentForSigning',
  async (
    {
      internalSignatureDocumentId,
      email,
      role,
      signature,
      location,
      fieldValues,
    }: {
      internalSignatureDocumentId: string;
      email: string;
      role: string;
      signature: string;
      location?: string;
      // The signer's own entries for this form's fillable fields (e.g. "Approved Credit Limit") —
      // see InternalSignatureDocumentFillableFields.tsx. Upserted by id into InternalSignatureDocument.fieldValues.
      fieldValues?: SignedFieldValue[];
    },
    thunkApi
  ) => {
    try {
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/internalSignatureDocumentsPublic/signInternalSignatureDocumentForSigning`,
        { internalSignatureDocumentId, email, role, signature, location, fieldValues },
        { headers: {} }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error?.message);
    }
  }
);

export const signInternalSignatureDocument = createAsyncThunk(
  'internalSignatureDocument/signInternalSignatureDocument',
  async (
    {
      supplierId,
      internalSignatureDocumentId,
      role,
      signature,
      location,
      fieldValues,
    }: {
      supplierId: string;
      internalSignatureDocumentId: string;
      role: InternalSignatureDocumentSignerRole;
      signature: string;
      location?: string;
      fieldValues?: SignedFieldValue[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/internalSignatureDocuments/signInternalSignatureDocument`,
        { internalSignatureDocumentId, role, signature, location, fieldValues },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg || error?.message);
    }
  }
);
